import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editHigherAdminFeedbackRequest } from "services/appealService";
import { HigherAdminFeedbackRequest } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  higherAdminFeedbackRequest?: HigherAdminFeedbackRequest;
  onClose: (refetch: boolean) => void;
};

const EditHigherAdminFeedback: FC<Props> = ({
  higherAdminFeedbackRequest,
  onClose,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<{
    requested_by: string;
    requested_by_id: string;
    request_summary: string;
    request_file: File | null;
  }>({
    requested_by: "",
    requested_by_id: "",
    request_summary: "",
    request_file: null,
  });

  useEffect(() => {
    if (higherAdminFeedbackRequest) {
      setFormState((prev) => ({
        ...prev,
        request_summary: higherAdminFeedbackRequest?.request_summary ?? "",
      }));
    }
  }, [higherAdminFeedbackRequest]);

  const handleChange = (e: any) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formState?.request_summary) return t("request_summary_required");

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const api = editHigherAdminFeedbackRequest;
    api({
      ...formState,
      id: higherAdminFeedbackRequest?.id || undefined,
      requested_by_id: undefined,
      requested_by: undefined,
    })
      .then(() => {
        showTooltip(t("successfully_edited"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {t("edit_feedback_request")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("higher_admin_submission_form")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("request_summary")}
            </label>
            <textarea
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="request_summary"
              rows={4}
              value={formState?.request_summary ?? ""}
              onChange={handleChange}
            />
          </div>

          <div className="mt-4 flex flex-row gap">
            <FileUpload
              label={t("request_file")}
              fileTypes="*"
              required={false}
              onChange={(file) =>
                setFormState((prev) => ({
                  ...prev,
                  request_letter_files: file,
                }))
              }
            />
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                t("edit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditHigherAdminFeedback;
